import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import Select from 'react-select';
import { Row, Col, Button } from 'reactstrap';
import moment from 'moment';
import Sidebar from '../../Sidebar';
import { useNavigate } from 'react-router';
import { useCookies } from 'react-cookie';
import { displayError } from '../../helpers';
import ErrorToast from '../../ErrorToast';
import FloatingControls from '../../components/FloatingControls';
import FormPageLayout from '../../components/FormPageLayout';
import LoadingOverlay from '../../components/LoadingOverlay';
import { getUserAllowedPlants } from '../../services/PlantServices';
import { getAllVendors } from '../../services/VendorServices';
import { getAllCustomerMain } from '../../services/CustomerMainServices';
import { getAllBank } from '../../services/BankService';
import { getAllGroups } from '../../services/GroupFormServices';
import {
    createPaymentVoucher,
    getDocumentsForCustomerPaymentVoucher,
    getDocumentsForPaymentVoucher,
    getPaymentVoucherNumber,
} from '../../services/PaymentVoucherServices';
import {
    AccountingHintPanel,
    PAYMENT_AGAINST,
    PAYMENT_MODES,
    PAYMENT_TYPES,
    getBankCashOptions,
    sumAllocation,
} from './VoucherUtils';
import { getCurrentDate } from '../../utils/DateUtils';

const PAYMENT_SUBTITLES = {
    vendor: 'Vendor payment against GRN — Dr Sundry Creditors, Cr Bank/Cash.',
    customer: 'Customer refund against credit note — Dr Sundry Debtors, Cr Bank/Cash.',
    expense: 'Expense payment — Dr Expense Ledger, Cr Bank/Cash.',
};

function PaymentVoucher() {
    const [cookies] = useCookies(['myToken']);
    const navigate = useNavigate();
    const [inputs, setInputs] = useState({
        payment_type: 'vendor',
        payment_date: getCurrentDate(),
        bank_date: getCurrentDate(),
        payment_mode: 'NEFT/RTGS',
        payment_against: 'invoice',
        payment_amount: '',
    });
    const [plants, setPlants] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [expenseLedgers, setExpenseLedgers] = useState([]);
    const [banks, setBanks] = useState([]);
    const [documentLines, setDocumentLines] = useState([]);
    const [accountingHint, setAccountingHint] = useState(null);
    const [inProgress, setInProgress] = useState(false);
    const [selectedPartyOption, setSelectedPartyOption] = useState(null);
    const [selectedBankOption, setSelectedBankOption] = useState(null);

    const paymentType = inputs.payment_type || 'vendor';
    const isExpense = paymentType === 'expense';
    const isCustomer = paymentType === 'customer';

    const partyOptions = useMemo(() => {
        if (paymentType === 'customer') {
            return customers;
        }
        if (paymentType === 'expense') {
            return expenseLedgers;
        }
        return vendors;
    }, [paymentType, customers, expenseLedgers, vendors]);

    const allocatedTotal = useMemo(
        () => sumAllocation(documentLines, 'payment_amt'),
        [documentLines],
    );

    const loadDocuments = useCallback(() => {
        if (!inputs.plant_id || inputs.payment_against !== 'invoice' || isExpense) {
            setDocumentLines([]);
            return;
        }
        if (paymentType === 'vendor' && inputs.vendor_id) {
            getDocumentsForPaymentVoucher(cookies, inputs.plant_id, inputs.vendor_id)
                .then((response) => {
                    setDocumentLines(response.document_lines || []);
                    setAccountingHint(response.accounting_hint || null);
                })
                .catch(() => setDocumentLines([]));
            return;
        }
        if (paymentType === 'customer' && inputs.customer_id) {
            getDocumentsForCustomerPaymentVoucher(cookies, inputs.plant_id, inputs.customer_id)
                .then((response) => {
                    setDocumentLines(response.document_lines || []);
                    setAccountingHint(response.accounting_hint || null);
                })
                .catch(() => setDocumentLines([]));
            return;
        }
        setDocumentLines([]);
    }, [cookies, inputs.plant_id, inputs.vendor_id, inputs.customer_id, inputs.payment_against, isExpense, paymentType]);

    useEffect(() => {
        getUserAllowedPlants(cookies).then((response) => {
            setPlants((response.plant_list || []).filter((p) => p.status).map((p) => ({
                value: p.id,
                label: p.plant_alias,
            })));
        });
        getAllVendors(cookies).then((response) => {
            setVendors((response.vendor_list || []).filter((v) => v.status).map((v) => ({
                value: v.id,
                label: v.name,
            })));
        });
        getAllCustomerMain(cookies).then((response) => {
            setCustomers((response.customer_list || []).filter((c) => c.status).map((c) => ({
                value: c.id,
                label: c.name,
            })));
        });
        getAllGroups(cookies).then((response) => {
            setExpenseLedgers((response.accounts_grouping_list || [])
                .filter((ledger) => ledger.entity_type === 'expenses' && ledger.parent_id)
                .map((ledger) => ({
                    value: ledger.id,
                    label: ledger.entity_name,
                })));
        });
        getAllBank(cookies).then((response) => {
            setBanks(getBankCashOptions(response.bank_list || []));
        });
    }, [cookies]);

    useEffect(() => {
        if (!inputs.plant_id || !inputs.payment_date) {
            return;
        }
        getPaymentVoucherNumber(
            cookies,
            inputs.plant_id,
            moment(inputs.payment_date).format('DD-MM-YYYY'),
        ).then((response) => {
            setInputs((values) => ({
                ...values,
                prefix: response.prefix,
                sl_no: response.payment_no,
                payment_no: `${response.prefix}/${response.payment_no}`,
            }));
        }).catch((error) => {
            displayError(error?.response?.data || error, 'Payment voucher number');
        });
    }, [cookies, inputs.plant_id, inputs.payment_date]);

    useEffect(() => {
        loadDocuments();
    }, [loadDocuments]);

    useEffect(() => {
        if (isExpense) {
            setAccountingHint({
                debit_ledger: 'Expense Ledger (Dr)',
                credit_ledger: 'Bank / Cash (Cr)',
                narration: 'Expense payment — charges operating cost and decreases bank/cash.',
            });
        }
    }, [isExpense]);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setInputs((values) => ({ ...values, [name]: value }));
    };

    const handlePaymentTypeChange = (event) => {
        const payment_type = event.target.value;
        setSelectedPartyOption(null);
        setDocumentLines([]);
        setInputs((values) => ({
            ...values,
            payment_type,
            vendor_id: '',
            customer_id: '',
            expense_id: '',
            payment_against: payment_type === 'expense' ? 'on_account' : values.payment_against,
        }));
    };

    const handleSelectChange = (name, option) => {
        if (name === 'bank_id') {
            setSelectedBankOption(option);
            setInputs((values) => ({ ...values, bank_id: option ? option.value : '' }));
            return;
        }
        if (name === 'plant_id') {
            setInputs((values) => ({ ...values, plant_id: option ? option.value : '' }));
            return;
        }
        setSelectedPartyOption(option);
        const partyId = option ? option.value : '';
        if (paymentType === 'vendor') {
            setInputs((values) => ({ ...values, vendor_id: partyId, customer_id: '', expense_id: '' }));
        } else if (paymentType === 'customer') {
            setInputs((values) => ({ ...values, customer_id: partyId, vendor_id: '', expense_id: '' }));
        } else {
            setInputs((values) => ({ ...values, expense_id: partyId, vendor_id: '', customer_id: '' }));
        }
    };

    const handleDocAmtChange = (index, value) => {
        setDocumentLines((lines) => lines.map((line, i) => (
            i === index ? { ...line, payment_amt: value } : line
        )));
    };

    const autoAllocate = () => {
        const amount = parseFloat(inputs.payment_amount) || 0;
        if (amount <= 0) {
            return;
        }
        let remaining = amount;
        setDocumentLines((lines) => lines.map((line) => {
            const alloc = Math.min(remaining, parseFloat(line.balance_amt) || 0);
            remaining -= alloc;
            return { ...line, payment_amt: alloc > 0 ? alloc.toFixed(2) : '' };
        }));
    };

    const partyLabel = isCustomer ? 'Customer' : (isExpense ? 'Expense Ledger' : 'Vendor');
    const allocationTitle = isCustomer ? 'Credit Note Allocation' : 'GRN Allocation';
    const docColLabel = isCustomer ? 'Credit Note No' : 'GRN No';
    const emptyDocMessage = isCustomer ? 'No outstanding credit notes' : 'No outstanding GRNs';

    const handleSubmit = (event) => {
        event.preventDefault();
        const paymentAmount = parseFloat(inputs.payment_amount) || 0;
        const hasParty = (paymentType === 'vendor' && inputs.vendor_id)
            || (paymentType === 'customer' && inputs.customer_id)
            || (paymentType === 'expense' && inputs.expense_id);

        if (!inputs.plant_id || !hasParty || !inputs.bank_id) {
            Swal.fire('Validation', `Plant, ${partyLabel} and Bank are required.`, 'warning');
            return;
        }
        if (paymentAmount <= 0) {
            Swal.fire('Validation', 'Payment amount must be greater than zero.', 'warning');
            return;
        }
        if (inputs.payment_against === 'invoice' && !isExpense) {
            if (Math.abs(allocatedTotal - paymentAmount) > 0.05) {
                Swal.fire('Validation', 'Document allocation must match payment amount.', 'warning');
                return;
            }
        }
        setInProgress(true);
        const payload = {
            plant_id: inputs.plant_id,
            payment_type: paymentType,
            vendor_id: inputs.vendor_id || null,
            customer_id: inputs.customer_id || null,
            expense_id: inputs.expense_id || null,
            bank_id: inputs.bank_id,
            prefix: inputs.prefix,
            sl_no: inputs.sl_no,
            payment_date: moment(inputs.payment_date).format('DD-MM-YYYY'),
            bank_date: moment(inputs.bank_date).format('DD-MM-YYYY'),
            payment_mode: inputs.payment_mode,
            payment_against: isExpense ? 'on_account' : inputs.payment_against,
            payment_amount: paymentAmount,
            instrument_no: inputs.instrument_no || '',
            instrument_date: inputs.instrument_date
                ? moment(inputs.instrument_date).format('DD-MM-YYYY') : '',
            remarks: inputs.remarks || '',
            document_lines: inputs.payment_against === 'invoice' && !isExpense
                ? documentLines
                    .filter((line) => parseFloat(line.payment_amt) > 0)
                    .map((line) => ({
                        gmr_id: line.gmr_id || null,
                        journal_id: line.journal_id || null,
                        invoice_id: line.invoice_id || null,
                        payment_amt: parseFloat(line.payment_amt),
                    }))
                : [],
        };
        createPaymentVoucher(cookies, payload)
            .then((response) => {
                Swal.fire('Saved', response.message || 'Payment voucher saved.', 'success');
                navigate(`/PaymentVoucherEdit/${response.id}`);
            })
            .catch((error) => displayError(error?.response?.data || error, 'Save Failed'))
            .finally(() => setInProgress(false));
    };

    return (
        <div id="outer-container" className="App">
            <Sidebar pageWrapId="page-wrap" outerContainerId="outer-container" />
            <div id="page-wrap">
                <FormPageLayout
                    moduleLabel="ACCOUNTS"
                    title="Payment Voucher"
                    subtitle={PAYMENT_SUBTITLES[paymentType]}
                    modeLabel="ADD MODE"
                >
                    <form onSubmit={handleSubmit}>
                        <AccountingHintPanel hint={accountingHint} />
                        <Row className="form-row table-bordered shadow p-2 my-2 border-secondary form-control-panel">
                            <Col sm={4}>
                                <label>Payment Type *</label>
                                <select className="form-control" name="payment_type" value={paymentType} onChange={handlePaymentTypeChange}>
                                    {PAYMENT_TYPES.map((opt) => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </Col>
                            <Col sm={4}>
                                <label>Plant *</label>
                                <Select
                                    options={plants}
                                    value={plants.find((p) => p.value === inputs.plant_id) || null}
                                    onChange={(opt) => handleSelectChange('plant_id', opt)}
                                    placeholder="Select plant"
                                />
                            </Col>
                            <Col sm={4}>
                                <label>Payment No</label>
                                <input className="form-control" value={inputs.payment_no || ''} readOnly />
                            </Col>
                        </Row>
                        <Row className="form-row table-bordered shadow p-2 my-2 border-secondary form-control-panel">
                            <Col sm={3}>
                                <label>Payment Date *</label>
                                <input type="date" className="form-control" name="payment_date" value={inputs.payment_date} onChange={handleChange} />
                            </Col>
                            <Col sm={5}>
                                <label>{partyLabel} *</label>
                                <Select
                                    options={partyOptions}
                                    value={selectedPartyOption}
                                    onChange={(opt) => handleSelectChange('party_id', opt)}
                                    placeholder={`Select ${partyLabel.toLowerCase()}`}
                                />
                            </Col>
                            <Col sm={4}>
                                <label>Pay From Bank / Cash *</label>
                                <Select
                                    options={banks}
                                    value={selectedBankOption}
                                    onChange={(opt) => handleSelectChange('bank_id', opt)}
                                    placeholder="Select bank or cash account"
                                />
                            </Col>
                        </Row>
                        <Row className="form-row table-bordered shadow p-2 my-2 border-secondary form-control-panel">
                            <Col sm={3}>
                                <label>Payment Mode *</label>
                                <select className="form-control" name="payment_mode" value={inputs.payment_mode} onChange={handleChange}>
                                    {PAYMENT_MODES.map((mode) => (
                                        <option key={mode} value={mode}>{mode}</option>
                                    ))}
                                </select>
                            </Col>
                            {!isExpense && (
                                <Col sm={3}>
                                    <label>Payment Against *</label>
                                    <select className="form-control" name="payment_against" value={inputs.payment_against} onChange={handleChange}>
                                        {PAYMENT_AGAINST.map((opt) => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </select>
                                </Col>
                            )}
                            <Col sm={isExpense ? 3 : 3}>
                                <label>Bank Date *</label>
                                <input type="date" className="form-control" name="bank_date" value={inputs.bank_date} onChange={handleChange} />
                            </Col>
                            <Col sm={isExpense ? 3 : 3}>
                                <label>Payment Amount *</label>
                                <input type="number" step="0.01" className="form-control" name="payment_amount" value={inputs.payment_amount} onChange={handleChange} />
                            </Col>
                        </Row>
                        <Row className="form-row table-bordered shadow p-2 my-2 border-secondary form-control-panel">
                            <Col sm={4}>
                                <label>Instrument No</label>
                                <input className="form-control" name="instrument_no" value={inputs.instrument_no || ''} onChange={handleChange} />
                            </Col>
                            <Col sm={4}>
                                <label>Instrument Date</label>
                                <input type="date" className="form-control" name="instrument_date" value={inputs.instrument_date || ''} onChange={handleChange} />
                            </Col>
                            <Col sm={4}>
                                <label>Remarks</label>
                                <input className="form-control" name="remarks" value={inputs.remarks || ''} onChange={handleChange} />
                            </Col>
                        </Row>

                        {inputs.payment_against === 'invoice' && !isExpense && (
                            <div className="form-row table-bordered shadow p-2 my-2 border-secondary form-control-panel">
                                <div className="d-flex justify-content-between align-items-center mb-2 px-2">
                                    <strong>{allocationTitle}</strong>
                                    <Button type="button" color="secondary" size="sm" onClick={autoAllocate}>
                                        Auto Allocate
                                    </Button>
                                </div>
                                <div className="table-responsive">
                                    <table className="table table-sm table-bordered mb-0">
                                        <thead>
                                            <tr>
                                                <th>{docColLabel}</th>
                                                <th>Date</th>
                                                <th>Amount</th>
                                                <th>Balance</th>
                                                <th>Payment Amt</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {documentLines.length === 0 ? (
                                                <tr><td colSpan={5} className="text-center text-muted">{emptyDocMessage}</td></tr>
                                            ) : documentLines.map((line, index) => (
                                                <tr key={line.gmr_id || line.journal_id || index}>
                                                    <td>{line.reference_no}</td>
                                                    <td>{line.reference_date}</td>
                                                    <td>{line.reference_amt}</td>
                                                    <td>{line.balance_amt}</td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            className="form-control form-control-sm"
                                                            value={line.payment_amt}
                                                            onChange={(e) => handleDocAmtChange(index, e.target.value)}
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot>
                                            <tr>
                                                <td colSpan={4} className="text-right"><strong>Allocated Total</strong></td>
                                                <td><strong>{allocatedTotal.toFixed(2)}</strong></td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            </div>
                        )}

                        <FloatingControls>
                            <Button color="primary" type="submit" disabled={inProgress}>Save</Button>
                            <Button color="secondary" type="button" onClick={() => navigate('/PaymentVoucherTable')}>Cancel</Button>
                        </FloatingControls>
                    </form>
                </FormPageLayout>
                <LoadingOverlay active={inProgress} />
                <ErrorToast />
            </div>
        </div>
    );
}

export default PaymentVoucher;
